// composables/commerce/useSupplierList.js
//
// The club's supplier roster for the suggestion sheet's picker — the read half
// of U-31, asked for in `2026-09-06-member-readable-supplier-list.md` and
// delivered as `GET /api/v1/suppliers` (owner decision L-G).
//
// ── Why every host fetches, and nothing is cached across opens ───────────────
//
// The backend BRIEFLY cached this — five minutes fresh, fifteen stale — and
// deliberately removed it, because that bought three saved queries against a
// table of tens of rows at the price of a real seam: the list she picked from
// ran up to a quarter of an hour behind while the `supplier_id` she submitted
// was validated LIVE, so a shop retired inside that window was still offered to
// her and then refused with a 422.
//
// A session cache on this side re-creates that seam exactly, and on a longer
// clock — a PWA session outlives fifteen minutes routinely. So this fetches when
// a sheet opens and keeps the result only while that sheet is open. It is tens
// of rows on a flow that already makes one request; the picker, the validator
// and the id echoed back on her item all read the same roster at the same
// instant, which is the property worth paying for.
//
// Concurrent opens still share ONE request (`inFlight`), because a double-tap is
// not a reason to ask twice.
//
// ── A failure USED TO BE "no list". It is now a closed door ─────────────────
//
// The endpoint is membership-gated: a Suppliers Club member, a pending member
// and a stranger each get a 403. Offline is a failure too.
//
// Until 2026-09-15 that was silent by design: the sheet fell back to a typed
// shop name, which was the complete path and the more valuable half of the
// feature. The backend then made BOTH suggestion doors ROSTER-ONLY — `supplier_id`
// is required and `supplier_name` is a 422 (brief
// `2026-09-15-roster-only-tips-once-per-line.md`, owner answer H-C). There is no
// longer anything behind the list, so a failed load is not "no list" any more,
// it is a door that cannot open.
//
// Hence `failed`. It separates the two states the sheet must now tell apart —
// "the club has no shops on its roster" and "we could not ask" — from the one
// they used to collapse into. The sheet says so and offers nothing rather than
// showing her an empty list and a send button that can only 422.
import { ref } from 'vue';
import { supplierListService } from '@/services/supplierList.service.js';
import { positiveIntOrNull } from '@/utils/supplierSuggestion.js';

// ── The suggestion reward at the door (BE→FE brief 2026-10-02-suggestion-reward)
//
// `rewardCents` is `meta.rewardCents` — null while the backend has the reward
// switched off (the key is absent) and also for 0, which is the same switch.
// `namedByMembers` rides a row only when the read was asked about a product, and
// only as a count of OTHER members; a row without it is simply not tagged.

/** A positive integer, or null — the one shape `rewardCents` may take. */
function rewardCentsOrNull(meta) {
  const value = Number(meta?.rewardCents);
  return meta != null && 'rewardCents' in meta && Number.isInteger(value) && value > 0
    ? value
    : null;
}

/**
 * @returns {{
 *   suppliers: import('vue').Ref<Array<{id: number, name: string, namedByMembers?: number}>>,
 *   rewardCents: import('vue').Ref<number|null>,
 *   loading: import('vue').Ref<boolean>,
 *   failed: import('vue').Ref<boolean>,
 *   load: (productId?: number|string|null) => Promise<Array<{id: number, name: string, namedByMembers?: number}>>,
 * }}
 */
export function useSupplierList() {
  const suppliers = ref([]);
  const rewardCents = ref(null);
  const loading = ref(false);
  // Did the LAST read fail? Distinct from `suppliers.length === 0`, which is
  // also what a club with an empty roster looks like — and the two need
  // different sentences now that the roster is the only path.
  const failed = ref(false);
  let inFlight = null;
  // What the in-flight read was ASKED about. A second open for a different
  // product must not ride the first one's response — its tags would be about
  // the wrong item.
  let inFlightKey = null;

  /**
   * A row is usable only with a positive integer id AND a name to render. The
   * id is what gets posted as `supplier_id`, and the name is the only thing she
   * can recognise — a row missing either is not a shop she can pick, so it is
   * dropped rather than rendered blank or posted as NaN.
   */
  function usableRows(rows) {
    if (!Array.isArray(rows)) return [];

    return rows.reduce((acc, row) => {
      // The SAME rule `openSupplierSuggestion` applies to the id read back off
      // her item. If the two disagreed about what counts as an id, a shop she
      // picked would fail to re-select on the next open.
      const id = positiveIntOrNull(row?.id);
      const name = typeof row?.name === 'string' ? row.name.trim() : '';
      if (id === null || name === '') return acc;

      // PRESENCE, not truth: the key is absent unless the read named a product,
      // and 0 is a real answer for a shop nobody named.
      const named = row?.namedByMembers;
      acc.push(
        Number.isInteger(named) && named >= 0 ? { id, name, namedByMembers: named } : { id, name },
      );
      return acc;
    }, []);
  }

  async function load(productId = null) {
    const key = productId == null ? null : String(productId);
    if (inFlight && inFlightKey === key) return inFlight;

    // EMPTIED BEFORE THE READ, not after it. Without this the second open of a
    // sheet renders the FIRST open's roster until the new response lands — a
    // smaller version of the very stale-list seam the no-cache decision exists
    // to prevent, and the one that ends in a 422 on a shop retired since.
    suppliers.value = [];
    rewardCents.value = null;
    failed.value = false;
    loading.value = true;
    inFlightKey = key;
    const request = supplierListService
      .list(productId)
      .then((res) => {
        // A newer read (another product) has taken over; this one is stale.
        if (inFlight !== request) return suppliers.value;
        rewardCents.value = rewardCentsOrNull(res?.data?.meta);
        // jsonApiClient hands back `{ data, meta, links }` with the deserialized
        // resources under `data` — an array here, one entry per shop.
        suppliers.value = usableRows(res?.data?.data);
        return suppliers.value;
      })
      .catch(() => {
        if (inFlight !== request) return suppliers.value;
        // 403 (not an approved seller), offline, 5xx — all one outcome for the
        // CALLER, but no longer a silent one: with the typed field gone there is
        // nothing behind an empty list, so the sheet has to be able to say we
        // could not ask.
        suppliers.value = [];
        failed.value = true;
        return suppliers.value;
      })
      .finally(() => {
        if (inFlight !== request) return;
        loading.value = false;
        inFlight = null;
        inFlightKey = null;
      });
    inFlight = request;

    return request;
  }

  return { suppliers, rewardCents, loading, failed, load };
}
